// "AI Dispatch" — photograph a dispatch screen, pick a photo, or paste raw
// text, and have the fields read out for review.
//
// The icon is a scan glyph, not the usual AI sparkle: this is a working tool
// for fire service admins, and glitter reads wrong on it. Scanning is also the
// honest metaphor — the feature reads a CAD screen.
//
// This never submits anything. It fills the form in and steps back; the admin
// reads what landed, fixes what is wrong, and presses Submit themselves. Fields
// the parser was unsure of are flagged so the eye goes to them first.
//
// The photo is sent, parsed, and dropped. It is not stored anywhere — not on the
// server, not in the incident, not in this component's state beyond the request.
import { useRef, useState } from "react";
import { Button } from "react-bootstrap";

import { api } from "includes";

export type ParsedDispatch = {
  fields: {
    locality: string;
    subLocality: string;
    address: string;
    box: string;
    incidentType: string;
    signal: string;
    respondingUnits: string[];
    description: string;
  };
  confidence: Record<string, "high" | "medium" | "low" | "none">;
  notes: string;
};

type Props = {
  auth?: string;
  onParsed: (result: ParsedDispatch) => void;
};

export default function DispatchIntake({ auth, onParsed }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  // Two inputs rather than one: `capture` opens the camera directly on a phone,
  // which is wrong when the admin wants a screenshot from their library.
  const cameraRef = useRef<HTMLInputElement>(null);
  const libraryRef = useRef<HTMLInputElement>(null);

  const send = (body: any, cType?: number) => {
    setBusy(true);
    setError("");
    api.call(
      {
        url: "admin/incident/parse-dispatch",
        method: "POST",
        auth: auth || "token",
        body,
        ...(cType ? { cType } : {}),
      },
      (res: any) => {
        setBusy(false);
        if (res.status === 200 && res.data) {
          onParsed(res.data);
          setOpen(false);
          setText("");
        } else {
          setError(res?.data?.error || "Could not read that dispatch");
        }
      }
    );
  };

  const onFile = (e: any) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice still fires a change.
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("That image is over 10MB — try a smaller one.");
      return;
    }
    const form = new FormData();
    form.append("image", file);
    // cType 6 is api.call's multipart case: it leaves the body untouched and
    // sets no Content-Type, so the browser writes the multipart boundary itself.
    send(form, 6);
  };

  return (
    <div className="fw-dispatch">
      {!open ? (
        <Button variant="light" className="w-100" onClick={() => setOpen(true)}>
          <i className="ri-scan-2-line me-1" />
          AI Dispatch
        </Button>
      ) : (
        <div className="card mb-2">
          <div className="card-body py-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <strong>
                <i className="ri-scan-2-line me-1" />
                AI Dispatch
              </strong>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setOpen(false)}
              />
            </div>

            <p className="text-muted mb-2">
              Fills the incident in for you. Check every field before submitting.
            </p>

            {/* Two paths, numbered, each sitting directly above the control it
                describes — on a phone the instruction and the button need to be
                readable in the same glance. */}
            <div className="mb-1">
              <strong>1. Photo</strong>
              <span className="text-muted"> — of the dispatch CAD screen.</span>
            </div>
            <div className="d-flex flex-wrap gap-2 mb-2">
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => cameraRef.current?.click()}
              >
                <i className="ri-camera-line me-1" />
                Take photo
              </Button>
              <Button
                variant="light"
                disabled={busy}
                onClick={() => libraryRef.current?.click()}
              >
                <i className="ri-image-line me-1" />
                Choose photo
              </Button>
            </div>

            {/* The dictation wording matters: the portal does no speech
                recognition and has no mic button of its own. Naming the phone's
                keyboard as where the microphone lives stops admins hunting this
                panel for a control that does not exist. */}
            <div className="mb-1">
              <strong>2. Text</strong>
              <span className="text-muted">
                {" "}
                — type or paste the details, or dictate them with the microphone
                on your phone&rsquo;s own keyboard.
              </span>
            </div>

            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={onFile}
            />
            <input
              ref={libraryRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onFile}
            />

            <textarea
              className="form-control mb-2"
              rows={3}
              placeholder="e.g. 10-75 Manhattan Box 0847, 250 W 54th St, E23 L4 BC9"
              value={text}
              disabled={busy}
              onChange={(e) => setText(e.target.value)}
            />
            <Button
              variant="primary"
              disabled={busy || !text.trim()}
              onClick={() => send({ text: text.trim() })}
            >
              {busy ? "Reading…" : "Read text"}
            </Button>

            <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.8125rem" }}>
              Photos are read and discarded — never saved, never attached to the
              incident.
            </p>

            {busy && (
              <p className="text-muted mt-1 mb-0">
                Reading the dispatch — this takes a few seconds.
              </p>
            )}
            {error && <p className="fw-field__error mt-2 mb-0">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
